import { Link, useParams } from "react-router-dom";
import { BANK_ACCOUNTS, GOZIS_WA_URL } from "../lib/constants";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, MessageCircle, QrCode, Calculator, HandCoins, Gift, Scale, Beef, Copy } from "lucide-react";
import { toast } from "sonner";

const CONTENT = {
  zakat: {
    title: "Zakat",
    icon: HandCoins,
    desc: "Zakat adalah kewajiban bagi setiap muslim yang telah mencapai nisab. NU Care LAZISNU Dawe menerima Zakat Profesi, Zakat Mal, Zakat Fitrah, dan Zakat Pertanian untuk disalurkan kepada mustahik sesuai syariah dan PSAK 109.",
  },
  infaq: {
    title: "Infaq / Sedekah",
    icon: Gift,
    desc: "Infaq dan sedekah adalah amalan mulia yang dapat dilakukan kapan saja. Salurkan infaq terbaik Anda melalui NU Care LAZISNU Dawe untuk program-program pemberdayaan umat di Kecamatan Dawe.",
  },
  fidyah: {
    title: "Fidyah / Kafarat",
    icon: Scale,
    desc: "Fidyah ditunaikan bagi yang tidak mampu berpuasa karena uzur, dan kafarat sebagai tebusan atas pelanggaran tertentu. LAZISNU Dawe menyalurkan fidyah/kafarat dalam bentuk paket pangan kepada yang berhak.",
  },
  qurban: {
    title: "Qurban",
    icon: Beef,
    desc: "Salurkan qurban Anda melalui NU Care LAZISNU Dawe. Hewan qurban disembelih dan didistribusikan kepada masyarakat yang membutuhkan di wilayah Kecamatan Dawe dan sekitarnya.",
  },
};

export default function InfoPage() {
  const { topic } = useParams();
  const content = CONTENT[topic] || CONTENT.infaq;
  const Icon = content.icon;

  const copyNumber = (num) => {
    navigator.clipboard?.writeText(num.replace(/\s/g, ""));
    toast.success("Nomor rekening disalin");
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`info-page-${topic}`}>
      <header className="border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <Link to="/"><img src="/logo-lazisnu.png" alt="Logo LAZISNU Dawe" className="h-10 w-10 rounded-lg bg-white object-contain p-0.5" /></Link>
          <div className="flex-1">
            <p className="font-heading font-bold text-slate-900 leading-tight">{content.title}</p>
            <p className="text-xs text-slate-500">NU Care LAZISNU Kecamatan Dawe</p>
          </div>
          <Link to="/">
            <Button variant="outline" size="sm" data-testid="info-back-button">
              <ArrowLeft className="mr-1.5 h-4 w-4" /> Kembali
            </Button>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-100 text-emerald-800">
                <Icon className="h-6 w-6" strokeWidth={1.8} />
              </div>
              <p className="text-sm text-slate-600 leading-relaxed">{content.desc}</p>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link to="/qris">
            <Button variant="outline" className="w-full" data-testid="info-goto-qris">
              <QrCode className="mr-2 h-4 w-4 text-emerald-800" /> Bayar via QRIS
            </Button>
          </Link>
          <a href={GOZIS_WA_URL} target="_blank" rel="noreferrer">
            <Button className="w-full bg-emerald-800 hover:bg-emerald-700 text-white" data-testid="info-goto-gozis">
              <MessageCircle className="mr-2 h-4 w-4" /> Go-ZIS (Jemput Donasi)
            </Button>
          </a>
          {topic === "zakat" && (
            <Link to="/kalkulator-zakat" className="sm:col-span-2">
              <Button variant="outline" className="w-full" data-testid="info-goto-kalkulator">
                <Calculator className="mr-2 h-4 w-4 text-emerald-800" /> Kalkulator Zakat
              </Button>
            </Link>
          )}
        </div>

        <Card>
          <CardContent className="p-6">
            <h2 className="font-heading text-lg font-semibold text-slate-800 mb-4">Transfer Rekening</h2>
            <div className="space-y-3">
              {BANK_ACCOUNTS.map((acc) => (
                <div key={acc.number} className="flex items-center gap-3 rounded-xl border border-slate-200 p-4">
                  <div className="flex h-10 w-14 shrink-0 items-center justify-center rounded-md bg-[#00529C] text-xs font-bold text-white">BRI</div>
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{acc.label}</p>
                    <p className="font-mono text-lg font-bold tracking-wider text-slate-900">{acc.number}</p>
                  </div>
                  <button
                    onClick={() => copyNumber(acc.number)}
                    className="rounded-md p-2 text-slate-400 transition-colors duration-200 hover:bg-emerald-50 hover:text-emerald-700"
                    data-testid={`info-copy-${acc.number.replace(/\s/g, "")}`}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
